// i18next-extract-mark-ns-start Step1
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { formatPrice } from '../../services/pricing';
import iconGarbage from '../../assets/order/iconGarbage.svg';
import {
  tabletThresholdPixels,
  FormInput as FormInputV2,
  Button as ButtonV3,
} from '../home/v3/styledComponents';
import { colors } from '../home/v4/styledComponents';

const Code = styled.div`
  font-weight: 300;
  font-size: 16px;
  color: ${colors.grey200};
  ${({ black }) => black && `color: ${colors.black}; margin-left: 0px; font-weight: 700;`};
   @media (max-width: ${tabletThresholdPixels}) {
     font-size: 14px;
    margin-left: 5px;
  }
`;
const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
`;
const Span = styled.span`
  color: ${colors.grey504};
  font-size: 16px;
  min-width: 50px;
`;
const TotalRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-weight: 700;
  font-size: 16px;
  text-transform: uppercase;
  margin: 32px 0;
  padding: ${({ vertical }) => vertical ? '0 20px' : '0 17px'};
  ${({ marginBottom }) => marginBottom && `margin-bottom: ${marginBottom};`}
  ${({ grey }) => grey && `color: ${colors.grey200}; `}

  @media (max-width: ${tabletThresholdPixels}) {
    margin: 20px 0px;
    ${({ marginBottom }) => marginBottom && `margin-bottom: ${marginBottom};`}
    padding: 0 12px;
  }
`;
const IconGarbage = styled.img`
    margin-right: 10px;
    cursor: pointer;
`;
const FormInput = styled(FormInputV2)`
  font-size: 12px;
  color: ${colors.black};
  width: 100%;
  height: 40px;
  margin-top: 0px;
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 17px;
  outline: none;
  @media (max-width: ${tabletThresholdPixels}) {
    z-index: 1;
    padding: 12px 17px;
    height: unset
  }
`;
const Button = styled(ButtonV3)`
  min-width: unset;
  min-height: unset;
  padding: 0 10px;
  font-size: 12px;
  &:hover {
    transform: none;
  }
  @media (max-width: ${tabletThresholdPixels}) {
    z-index: 1;
    margin: 0px;
    width: 35%;
    font-size: 12px;
  }
`;
const ErrorP = styled.p`
  font-size: 12px;
  color: ${colors.red500};
  margin: 10px 0 0 17px;
  @media (max-width: ${tabletThresholdPixels}) {
  }
`;
const PromoCodeDiv = styled.div`
  ${({ vertical }) => vertical ? 'margin-top: 32px;' : 'margin-top: 40px;'};
`;
const PromoCodeForm = styled.div`
  display: flex;
  gap: 10px;
  ${({ vertical }) => !vertical && '  margin: 0 17px;'};
  @media (max-width: ${tabletThresholdPixels}) {
    margin: 0;
  }
`;

export default function OrderSummaryPromoCode({
  promoCode, vertical, total, totalPaid, totalPaidBrand, isPromoCodeDisplayed, lastStep, checkPromoCode,
  errorPromoCode, removePromoCode, setInputPromoCode, discountVouchersValue, currency, repairBonus,
}) {
  const { t } = useTranslation();
  const totalAfterRepairBonus = total - repairBonus;
  const renderPromoCodeForm = () => (
    <PromoCodeDiv vertical={vertical}>
      <PromoCodeForm vertical={vertical}>
        <FormInput
          name="promoCode"
          value={promoCode.code || ''}
          type="text"
          placeholder={t('J‘ai un code promo')}
          onChange={(event) => setInputPromoCode(event.target.value)}
        />
        <Button black onClick={() => checkPromoCode(promoCode.code)}>{t('Valider')}</Button>
      </PromoCodeForm>
      {errorPromoCode && <ErrorP>{t('Ce code promo n’est pas valide, vérifiez bien les majuscules/minuscules')}</ErrorP>}
    </PromoCodeDiv>
  );

  return (
    <div>
      {(promoCode?.value > 0 && !Number.isNaN(promoCode.value)) && totalAfterRepairBonus !== totalPaid && (
        <TotalRow vertical={vertical} marginBottom="0">
          <FlexContainer>
            {!lastStep && <IconGarbage src={iconGarbage} alt="" onClick={() => removePromoCode()} />}
            {promoCode?.brand?.name ? (
              `${promoCode?.brand?.name} vous offre`
            ) : (
              <>
                <Code black>{t('Code promo')}</Code>
                {isPromoCodeDisplayed && <Code>{promoCode.code}</Code>}
              </>
            )}
          </FlexContainer>
          <Span>
            {
              promoCode.unit === 'EUROS'
                ? `- ${formatPrice(promoCode.value, currency)}`
                : `- ${promoCode.value}%`
            }
          </Span>
        </TotalRow>
      )}

      {promoCode?.value === 0 && total !== totalPaid && promoCode?.brand?.name && (
        <TotalRow vertical={vertical}>
          {!lastStep && <IconGarbage src={iconGarbage} alt="" onClick={() => removePromoCode()} />}
          <FlexContainer>{`${promoCode?.brand?.name} vous offre`}</FlexContainer>
          <Span>{`${formatPrice(totalPaidBrand, currency)}`}</Span>
        </TotalRow>
      )}

      {discountVouchersValue > 0 && (
        <TotalRow vertical={vertical}>
          {t('Crédits parrainage')}
          <Span>{`- ${formatPrice(discountVouchersValue, currency)}`}</Span>
        </TotalRow>
      )}
      {(!promoCode || promoCode?.value === undefined) && !lastStep && renderPromoCodeForm()}
    </div>
  );
}

OrderSummaryPromoCode.propTypes = {
  promoCode: PropTypes.shape({
    value: PropTypes.number,
    unit: PropTypes.string,
    code: PropTypes.string,
    brand: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  vertical: PropTypes.bool,
  total: PropTypes.number.isRequired,
  totalPaid: PropTypes.number.isRequired,
  totalPaidBrand: PropTypes.number.isRequired,
  isPromoCodeDisplayed: PropTypes.bool,
  lastStep: PropTypes.bool,
  checkPromoCode: PropTypes.func.isRequired,
  errorPromoCode: PropTypes.bool,
  removePromoCode: PropTypes.func.isRequired,
  setInputPromoCode: PropTypes.func.isRequired,
  discountVouchersValue: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  repairBonus: PropTypes.number,
};

OrderSummaryPromoCode.defaultProps = {
  promoCode: null,
  vertical: false,
  isPromoCodeDisplayed: false,
  lastStep: false,
  errorPromoCode: '',
  repairBonus: 0,
};
